import { cn } from "cn";

import { keysFor, type Keys } from "./keymap.ts";

/**
 * Una combinación de teclas, dibujada tecla por tecla.
 *
 * Está en los botones y en las cabeceras de los paneles para que el atajo se
 * aprenda mirando: lo que se ve a diario se acaba tecleando sin mirar. Se
 * dibuja como en esta plataforma (`keysFor`): en Mac, ⌃ ⌥ ⇧ ⌘.
 */
export function Kbd({
  keys,
  className,
}: {
  readonly keys: Keys;
  readonly className?: string;
}) {
  return (
    <kbd
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 font-sans text-[0.68rem] leading-none font-normal text-muted-foreground",
        className,
      )}
    >
      {keysFor(keys).map((key, index) => (
        <span
          // Las teclas de una combinación no se reordenan.
          key={`${key}-${String(index)}`}
          className="rounded-[4px] border border-b-2 bg-background px-1 py-0.5"
        >
          {key}
        </span>
      ))}
    </kbd>
  );
}

/** Los nombres de `KeyboardEvent.key`, que pide ARIA, de cada insignia. */
const ARIA_NAMES: Record<string, string> = {
  "↑": "ArrowUp",
  "↓": "ArrowDown",
  "+": "Plus",
  "-": "Minus",
  Esc: "Escape",
  Ctrl: "Control",
  "⌃": "Control",
  "⌥": "Alt",
  "⇧": "Shift",
  "⌘": "Meta",
  "↩": "Enter",
  "⌫": "Backspace",
};

/**
 * El texto de `aria-keyshortcuts`, de lo que se pulsa en esta plataforma:
 * `Alt+Shift+1` en un PC, `Control+Shift+1` en Mac.
 */
export const ariaKeys = (...combos: readonly Keys[]): string =>
  combos
    .map((keys) =>
      keysFor(keys)
        .map((key) => ARIA_NAMES[key] ?? key)
        .join("+"),
    )
    .join(" ");
